/**
 * Strategies for handling different types of placeholders in SVG testimonials
 */

import { ensureDefs, createSvgElement, createXhtmlElement, createForeignObject } from './svgUtils'
import { isEmptyContent, setElementContent, calculateFittingFontSize } from './contentUtils'

export interface TextStyles {
  color: string
  fontFamily: string
  fontSize: string
  fontStyle: string
  fontWeight: string
  lineHeight: string
}

/**
 * Base interface for placeholder strategies
 */
export interface PlaceholderStrategy {
  canHandle(element: Element, placeholderId: string): boolean
  process(element: Element, content: string | null, svgDoc: Document): void
}

/**
 * Handles image-based headshot placeholders
 */
export class HeadshotStrategy implements PlaceholderStrategy {
  /**
   * Determines if this strategy can handle the given element
   * @param element - The SVG element to check
   * @param placeholderId - The placeholder ID
   * @returns True if this strategy handles HEADSHOT placeholders
   */
  canHandle(element: Element, placeholderId: string): boolean {
    return placeholderId === 'HEADSHOT'
  }

  /**
   * Processes the HEADSHOT placeholder by creating an image pattern
   * @param element - The SVG element with HEADSHOT id
   * @param content - The avatar URL or null
   * @param svgDoc - The SVG document
   */
  process(element: Element, content: string | null, svgDoc: Document): void {
    // If avatarUrl is null or undefined, remove the element
    if (content === null || content === undefined) {
      element.parentNode?.removeChild(element)
      return
    }

    // Generate a unique ID for our pattern using a random string with the expected prefix
    const patternId = `headshot-pattern-${Math.random().toString(36).substring(2, 15)}`

    // Create pattern element with objectBoundingBox units for scaling to path bounds
    const pattern = createSvgElement('pattern', svgDoc)
    pattern.setAttribute('id', patternId)
    pattern.setAttribute('patternContentUnits', 'objectBoundingBox')
    pattern.setAttribute('width', '1')
    pattern.setAttribute('height', '1')

    // Create image element inside pattern
    const image = createSvgElement('image', svgDoc)
    image.setAttribute('href', content)
    image.setAttribute('x', '0')
    image.setAttribute('y', '0')
    image.setAttribute('width', '1')
    image.setAttribute('height', '1')
    image.setAttribute('preserveAspectRatio', 'xMidYMid slice')

    pattern.appendChild(image)

    // Add pattern to defs
    const defs = ensureDefs(svgDoc)
    defs.appendChild(pattern)

    // Update the path's fill to reference our new pattern
    element.setAttribute('fill', `url(#${patternId})`)
  }
}

/**
 * Extracts text styles from an SVG text element
 * @param textElement
 */
export function extractTextStyles(textElement: Element): TextStyles {
  const initialFontSize = parseFloat(textElement.getAttribute('font-size') || '16')
  return {
    color: textElement.getAttribute('fill') || 'black',
    fontFamily: textElement.getAttribute('font-family') || 'Arial',
    fontSize: `${initialFontSize}px`,
    fontStyle: textElement.getAttribute('font-style') || 'normal',
    fontWeight: textElement.getAttribute('font-weight') || '400',
    lineHeight: '1.2', // Set a default line height ratio
  }
}

/**
 * Creates a div element styled for text content
 * @param svgDoc
 * @param styles
 * @param content
 */
export function createStyledDiv(
  svgDoc: Document,
  styles: TextStyles,
  content: string | null,
): HTMLElement {
  const div = createXhtmlElement('div', svgDoc) as HTMLElement
  div.style.width = '100%'
  div.style.height = '100%'
  div.style.overflow = 'hidden'

  // Apply styles
  Object.assign(div.style, styles)

  if (content && !isEmptyContent(content)) {
    setElementContent(div, content)
  }

  return div
}

/**
 * Base class for rectangle-related logic shared between strategies
 */
export abstract class RectBasedStrategy implements PlaceholderStrategy {
  abstract canHandle(element: Element, placeholderId: string): boolean
  abstract process(element: Element, content: string | null, svgDoc: Document): void

  /**
   * Get a rectangle element from a group, either directly or derived from a path
   * @param group
   * @param svgDoc
   */
  protected getRectFromGroup(group: Element, svgDoc: Document): Element | null {
    let rect = group.querySelector('rect')

    if (!rect) {
      rect = this.createRectFromPath(group, svgDoc)
    }

    return rect
  }

  /**
   * Creates a rectangle element based on the bounding box of a path
   * @param group
   * @param svgDoc
   */
  protected createRectFromPath(group: Element, svgDoc: Document): Element | null {
    const path = group.querySelector('path')
    if (!path) return null

    // Create a clone of the SVG for measurement purposes only
    const tempSvg = svgDoc.documentElement.cloneNode(true) as SVGSVGElement

    // Append to DOM temporarily
    document.body.appendChild(tempSvg)

    // Find the equivalent path in the temp SVG
    const tempGroup = tempSvg.getElementById(group.id)
    const tempPath = tempGroup ? tempGroup.querySelector('path') : null

    let rect = null
    if (tempPath) {
      // Get dimensions from the temporary path
      const pathBox = tempPath.getBBox()

      // Create rect with the correct dimensions
      rect = createSvgElement('rect', svgDoc)
      rect.setAttribute('x', pathBox.x.toString())
      rect.setAttribute('y', pathBox.y.toString())
      rect.setAttribute('width', pathBox.width.toString())
      rect.setAttribute('height', pathBox.height.toString())
    }

    // Remove the temp SVG
    document.body.removeChild(tempSvg)

    return rect
  }
}

/**
 * Strategy for handling complex placeholder structures with text and rectangle/path
 */
export class ComplexPlaceholderStrategy extends RectBasedStrategy {
  /**
   *
   * @param element
   * @param _placeholderId
   */
  canHandle(element: Element, _placeholderId: string): boolean {
    // Complex structure has both text and rect/path
    const hasTextElement = !!element.querySelector('text')
    const hasRectOrPath = !!element.querySelector('rect') || !!element.querySelector('path')
    return hasTextElement && hasRectOrPath
  }

  /**
   *
   * @param element
   * @param content
   * @param svgDoc
   */
  process(element: Element, content: string | null, svgDoc: Document): void {
    if (isEmptyContent(content)) {
      element.parentNode?.removeChild(element)
      return
    }

    // Get positioning from the rect
    const rect = this.getRectFromGroup(element, svgDoc)
    if (!rect) {
      throw new Error(`No rectangle found in ${element.id} placeholder`)
    }

    // Get styles from the text
    const textElement = element.querySelector('text')
    if (!textElement) {
      throw new Error(`No text element found in ${element.id} placeholder`)
    }

    // Get dimensions and position
    const rectX = rect.getAttribute('x') || '0'
    const rectY = rect.getAttribute('y') || '0'
    const rectWidth = parseFloat(rect.getAttribute('width') || '0')
    const rectHeight = parseFloat(rect.getAttribute('height') || '0')
    const transform = rect.getAttribute('transform') || ''

    // Create a foreignObject with the same dimensions
    const foreignObject = createForeignObject(
      svgDoc,
      rectX,
      rectY,
      rectWidth,
      rectHeight,
      transform,
    )

    // Style the content div
    const styles = extractTextStyles(textElement)
    const div = createStyledDiv(svgDoc, styles, content)

    // Calculate and apply appropriate font size
    const initialFontSize = parseFloat(styles.fontSize)
    const fittingFontSize = calculateFittingFontSize(
      div as HTMLElement,
      initialFontSize,
      rectWidth,
      rectHeight,
    )
    div.style.fontSize = `${fittingFontSize}px`

    // Put it all together
    foreignObject.appendChild(div)
    element.parentNode?.replaceChild(foreignObject, element)
  }
}

/**
 * Strategy for simple text placeholders
 */
export class SimpleTextStrategy implements PlaceholderStrategy {
  /**
   *
   * @param _element
   * @param _placeholderId
   */
  canHandle(_element: Element, _placeholderId: string): boolean {
    // Default strategy for any element not handled by other strategies
    return true
  }

  /**
   *
   * @param element
   * @param content
   * @param _svgDoc
   */
  process(element: Element, content: string | null, _svgDoc: Document): void {
    if (isEmptyContent(content)) {
      element.parentNode?.removeChild(element)
      return
    }

    const tspan = element.querySelector('tspan')
    if (tspan) {
      tspan.textContent = content
    } else {
      // If there's no tspan, set the text content directly
      element.textContent = content
    }
  }
}
